import * as React from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  lighten,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Buildings as BuildingsIcon, Eye as EyeIcon, Plus as PlusIcon } from '@phosphor-icons/react';

import { CreateCommunityDialog } from './create-community-dialog';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';

import type { Community } from '@/types/v2/community';
import { paths } from '@/paths';

interface CommunitiesTableProps {
  communities: Community[];
  loading: boolean;
  onUpdated?: () => void;
}

export function CommunitiesTable({
  communities,
  loading,
}: CommunitiesTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const columns = useMemo<MRT_ColumnDef<Community>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 180,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        size: 140,
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
      },
      {
        accessorKey: 'state',
        header: 'State',
        size: 80,
      },
      {
        id: 'discussion_page_enabled',
        header: 'Discussion',
        size: 100,
        accessorFn: (row) => (row.discussion_page_enabled ? 'Enabled' : 'Disabled'),
      },
      {
        id: 'deposits_status',
        header: 'Deposits',
        size: 110,
        accessorFn: (row) => (row.payments_enabled ? 'Enabled' : 'Disabled'),
      },
      {
        id: 'stripe_status',
        header: 'Stripe',
        size: 140,
        accessorFn: (row) => {
          if (!row.stripe_connect_account_id) return 'Not connected';
          if (row.stripe_connect_charges_enabled && row.stripe_connect_onboarding_complete) return 'Active';
          if (row.stripe_connect_onboarding_complete) return 'Pending review';
          return 'Incomplete';
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable<Community>({
    columns,
    data: communities,
    initialState: {
      showGlobalFilter: true,
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <IconButton
        onClick={() =>
          navigate(
            paths.settings.account.communities.community(
              row.original.uuid ?? row.original.ulid ?? String(row.original.id)
            )
          )
        }
        size="small"
        aria-label="View community"
      >
        <EyeIcon size={20} />
      </IconButton>
    ),
    renderTopToolbar: ({ table }) => (
      <Box
        sx={(theme) => ({
          backgroundColor: lighten(theme.palette.background.default, 0.05),
          display: 'flex',
          gap: '0.5rem',
          p: '8px',
          justifyContent: 'space-between',
        })}
      >
        <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <MRT_GlobalFilterTextField table={table} />
          <MRT_ToggleFiltersButton table={table} />
        </Box>
      </Box>
    ),
  });

  return (
    <React.Fragment>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BuildingsIcon
                size={24}
                style={{ marginRight: '8px', color: 'var(--mui-palette-primary-main)' }}
              />
              <Typography sx={{ fontWeight: 600, color: 'text.primary' }} variant="subtitle1">
                {communities.length} {communities.length === 1 ? 'Community' : 'Communities'}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            startIcon={<PlusIcon size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            New Community
          </Button>
        </Box>

        <MaterialReactTable table={table} />
      </Paper>

      <CreateCommunityDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </React.Fragment>
  );
}
